import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Atmosphere } from "../components/Atmosphere";
import { ChevronRight, ArrowDown } from "lucide-react";
import { characters } from "../data/characters1";
import castleBackground from "./castlebackground.png";
import redLantern from "./redlantern.png";
import logoImg from "../components/RoyalKingdoms.png";
import yinYangImg from "./YinYang.png";

export default function Home() {
  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={castleBackground}
            alt={currentLang === "en" ? "Ruìhuá in the fog" : "Ruìhuá im Nebel"}
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.65) contrast(1.05) saturate(0.85)" }}
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <Atmosphere />
        
        <div className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="text-center max-w-4xl flex flex-col items-center"
          >

            {/* Das Logo ersetzt den H1-Schriftzug */}
            <div className="mb-12 flex justify-center">
              <img 
                src={logoImg} 
                alt="Royal Kingdoms" 
                className="h-20 sm:h-64 md:h-68 w-auto object-contain drop-shadow-[0_4px_12px_rgba(201,162,39,0.15)]"
              />
            </div>

            <p className="serif italic text-[#B3B6B7] text-xl sm:text-2xl max-w-2xl mx-auto leading-relaxed mb-12">
              {currentLang === "en" 
                ? '"Discover a world full of magic, love, and claims to power — in a time where friendship and solidarity are the most important path to victory."'
                : '"Entdecke eine Welt voller Magie, Liebe und Machtansprüche — In einer Zeit in der Freundschaft und Zusammenhalt das wichtigste Mittel zum Sieg ist."'
              }
            </p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/welt" data-testid="enter-ruihua-button">
                <button className="btn-imperial">
                  {currentLang === "en" ? "Enter Ruìhuá" : "Betrete Ruìhuá"} <ChevronRight size={16} />
                </button>
              </Link>
              <Link to="/charaktere" data-testid="meet-characters-button">
                <button className="btn-ghost">
                  {currentLang === "en" ? "Meet the Heirs" : "Triff die Thronfolgenden"}
                </button>
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 2, duration: 1 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#E8DDC8]"
          >
            <span className="text-xs tracking-[0.3em] uppercase">
              {currentLang === "en" ? "Descend" : "Hinabsteigen"}
            </span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ArrowDown size={16} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INTRO SECTION */}
      <section className="relative py-32 px-6" data-testid="intro-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="grid lg:grid-cols-12 gap-10 items-center"
          >
            <div className="lg:col-span-7 space-y-6">
              <p className="text-[#C9A227] text-sm tracking-[0.4em] uppercase">
                {currentLang === "en" ? "A Chronicle in Six Volumes" : "Eine Chronik in sechs Bänden"}
              </p>
              <h2 className="serif text-4xl lg:text-6xl text-[#E8DDC8] leading-tight">
                {currentLang === "en" ? (
                  <>A Realm of<br /><span className="italic text-[#B01218]">Sand & Ash</span></>
                ) : (
                  <>Ein Reich aus<br /><span className="italic text-[#B01218]">Sand & Asche</span></>
                )}
              </h2>
              <p className="text-[#B3B6B7] text-lg leading-relaxed">
                {currentLang === "en"
                  ? "Ruìhuá is no weak realm. It has overcome many threats, yet it is gradually sinking into desolation. Only Zīxuě—the future empress—can lead the realm out of the shadows and forge alliances unlike any seen before. The kingdoms unite for an unprecedented adventure. This is the world we are about to enter."
                  : "Ruìhuá ist kein schwaches Reich. Es hat schon viele Bedrohungen überwunden und doch versinkt es nach und nach in Trostlosigkeit.  Nur Zīxuě - die zukünftige Kaiserin - kann das Reich wieder aus seinem Schatten holen und Allianzen gründen, die es so noch nie gegeben hat. Die Königreiche verbünden sich zu einem Abenteuer, das es so noch nie gegeben hat. Dies ist die Welt, in die wir eintreten werden."
                }
              </p>
              <div className="flex gap-8 pt-4">
                <div>
                  <p className="serif text-4xl text-[#C9A227]">12</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#7A7C7D] mt-1">
                    {currentLang === "en" ? "Realms" : "Reiche"}
                  </p>
                </div>
                <div>
                  <p className="serif text-4xl text-[#C9A227]">06</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#7A7C7D] mt-1">
                    {currentLang === "en" ? "Volumes" : "Bände"}
                  </p>
                </div>
                <div>
                  <p className="serif text-4xl text-[#C9A227]">03</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#7A7C7D] mt-1">
                    {currentLang === "en" ? "Worlds" : "Welten"}
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="aspect-[3/4] relative overflow-hidden">
                <img
                  src={redLantern}
                  alt={currentLang === "en" ? "Lanterns" : "Laternen"}
                  className="w-full h-full object-cover"
                  style={{ filter: "saturate(0.85) brightness(0.3)" }}
                />
                <div className="corner-orn tl" />
                <div className="corner-orn tr" />
                <div className="corner-orn bl" />
                <div className="corner-orn br" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-24 px-6" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <div className="divider-gold mb-16" />
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                title: currentLang === "en" ? "The Kingdoms" : "Die Herrschaftgebiete",
                chinese: "領土", // Geschlechtsneutral für "Reiche/Territorien"
                desc: currentLang === "en" 
                  ? "The realms and territories of Ruìhuá. Here, the heirs to the throne rule, each from their own residence."
                  : "Die Reiche und Gebiete Ruìhuás. Hier Regieren die Throfolgenden. Jeder aus seiner eigenen Residenz heraus.",
                to: "/welt",
                test: "pillar-kingdoms",
              },
              {
                title: currentLang === "en" ? "The Characters" : "Die Charaktere",
                chinese: "人物",
                desc: currentLang === "en"
                  ? "The Over- and Underbounds of this world. A bond forged by the gods. Linked through the web of Elyra."
                  : "Die Ober- und Unterwürfigen dieser Welt. Eine Bindung von den Göttern erschaffen. Verbunden über das Netzt des Elyra.",
                to: "/charaktere",
                test: "pillar-characters",
              },
              {
                title: currentLang === "en" ? "The OB · UB System" : "Das OW · UW System",
                chinese: "上下",
                desc: currentLang === "en"
                  ? "Social dynamics between the Overbound (OB) and the Underbound (UB)."
                  : "Gesellschaftliche Dynamik zwischen Oberwürfigen und Unterwürfigen.",
                to: "/system",
                test: "pillar-system",
              },
            ].map((p, idx) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: idx * 0.15 }}
              >
                <Link to={p.to} data-testid={p.test} className="block">
                  <div className="card-imperial p-10 h-full relative">
                    <div className="corner-orn tl" />
                    <div className="corner-orn br" />
                    <p className="chinese text-[#C9A227] text-3xl mb-4">{p.chinese}</p>
                    <h3 className="serif text-3xl text-[#E8DDC8] mb-4">{p.title}</h3>
                    <p className="text-[#B3B6B7] text-base leading-relaxed mb-8">
                      {p.desc}
                    </p>
                    <div className="flex items-center gap-2 text-[#C9A227] text-sm tracking-[0.25em] uppercase">
                      {currentLang === "en" ? "Explore" : "Erkunden"} <ChevronRight size={14} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CHARACTER PREVIEW */}
      <section className="relative py-24 px-6" data-testid="characters-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="divider-gold mb-16" />
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="chinese text-[#C9A227] text-2xl mb-2">人物</p>
              <h2 className="serif text-4xl lg:text-5xl text-[#E8DDC8]">
                {currentLang === "en" ? (
                  <>Souls of <span className="italic text-[#C9A227]">Elyra</span></>
                ) : (
                  <>Seelen des <span className="italic text-[#C9A227]">Elyra</span></>
                )}
              </h2>
              <p className="text-[#B3B6B7] mt-3 max-w-xl">
                {currentLang === "en"
                  ? "Each character carries within themselves a power derived from the pure powers of the gods."
                  : "Jeder Charakter trägt eine Kraft in sich von den Reinkräften der Götter."}
              </p>
            </div>
            <Link to="/charaktere" data-testid="home-all-characters">
              <button className="btn-ghost">
                {currentLang === "en" ? "View all" : "Alle ansehen"} <ChevronRight size={14} />
              </button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {characters.slice(0, 6).map((c, idx) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
              >
                <Link
                  to={`/charaktere/${c.slug}`}
                  className="block relative aspect-[3/4] overflow-hidden border border-[#C9A227]/20 group"
                  data-testid={`home-char-${c.slug}`}
                >
                  <img
                    src={`/characters/${c.slug}.jpg`}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-all duration-[1200ms] group-hover:scale-105"
                    style={{ filter: 'saturate(0.8) brightness(0.8) contrast(1.05)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-transparent" />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `linear-gradient(180deg, ${c.accent}22 0%, transparent 50%)` }}
                  />
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="chinese text-sm" style={{ color: c.accent }}>{c.chinese}</p>
                    <p className="serif text-lg text-[#E8DDC8] leading-tight">{c.name}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE BANNER */}
      <section className="relative py-32 px-6 overflow-hidden" data-testid="quote-section">
        <div className="absolute inset-0 opacity-30">
          <Atmosphere variant="minimal" />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <p className="chinese text-[#C9A227] text-5xl mb-8">緣分</p>
          <p className="serif italic text-3xl lg:text-5xl text-[#E8DDC8] leading-tight">
            {currentLang === "en" ? (
              <>
                "I don't need a noble whore who drops to her knees for the first title that comes along. I need someone with backbone.
                <br />
                <span className="text-[#C9A227]">
                  Someone who can rule an entire empire by my side!"
                </span>
              </>
            ) : (
              <>
                „Ich brauche keine adelige Hure, die sich für den erstbesten Adelstitel auf die Knie wirft. Ich brauche jemanden mit Rückgrat.
                <br />
                <span className="text-[#C9A227]">
                  Jemanden, der es schafft, ein ganzes Reich mit mir zu regieren!"
                </span>
              </>
            )}
          </p>
          <p className="mt-10 text-[#7A7C7D] text-sm tracking-[0.3em] uppercase">
            {currentLang === "en" ? "— From Volume I: The Mighty Kingdom" : "— Aus Band I: Das mächtige Königreich"}
          </p>
        </motion.div>
      </section>

      {/* Test */}
      <section 
        className="relative py-40 px-6 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${yinYangImg})` }}
        data-testid="cta-section"
      >
        {/* Dunkler Overlay zur besseren Lesbarkeit des Textes */}
        <div className="absolute inset-0 bg-black/60 z-0" />

        {/* Content-Container */}
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="serif text-4xl lg:text-5xl text-[#E8DDC8] mb-6">
            {currentLang === "en" ? (
              <>Are you <span className="italic text-[#C9A227]">OB</span> or <span className="italic text-[#B01218]">UB</span>?</>
            ) : (
              <>Bist du <span className="italic text-[#C9A227]">OW</span> oder <span className="italic text-[#B01218]">UW</span>?</>
            )}
          </h2>
          <p className="text-[#B3B6B7] mb-10">
            {currentLang === "en"
              ? "A short mirror of the soul. Ten questions are enough."
              : "Ein kurzer Spiegel der Seele. Was definiert dich?."}
          </p>
          <Link to="/test" data-testid="start-quiz-cta">
            <button className="btn-imperial inline-flex items-center gap-2">
              {currentLang === "en" ? "Look into the mirror" : "Den Spiegel betrachten"} <ChevronRight size={16} />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}